import { QueryKey } from "@tanstack/react-query";

/**
 * Which sibling read queries a mutation invalidates by default:
 * - `"entity"` — only reads of the mutation's own entity (or an ancestor of it):
 *   `updateAccount` → `getAccount` / `listAccounts` / `searchAccounts`, but not
 *   child reads like `getAccountSettings`. The precise, low-traffic default.
 * - `"api"` — every read on the same api (the `[apiName]` prefix). Broad and
 *   never-stale, at the cost of refetching unrelated collections.
 * - `"none"` — inject nothing; the call's own `dependencies` are the only ones.
 */
export type V3DefaultDependenciesLevel = "api" | "entity" | "none";

/**
 * The precomputed default-dependency keys for a mutation, one set per selectable
 * level. `entity` is the matched sibling reads, `api` is the whole-api prefix;
 * `"none"` resolves to `[]` and so needs no stored set.
 */
export type V3DefaultDependencies = {
  api: QueryKey[];
  entity: QueryKey[];
};

/**
 * The PascalCase entity root of an SDK method name — everything from the first
 * uppercase letter on, i.e. the noun after the leading camelCase verb.
 * `updateAccount` → `"Account"`, `listAccounts` → `"Accounts"`,
 * `setAccountPrincipal` → `"AccountPrincipal"`. Returns `""` when there is no noun.
 */
export const entityRootOf = (methodName: string): string => {
  const match = methodName.match(/[A-Z].*$/);
  return match ? match[0] : "";
};

/**
 * Best-effort singularization so a plural list noun matches the singular entity:
 * `Accounts` → `Account`, `Companies` → `Company`, `Addresses` → `Address`.
 * A heuristic, not a linguistics engine — both sides of a comparison are run
 * through it, so even an imperfect stem still matches itself consistently.
 */
const singularize = (noun: string): string => {
  if (noun.endsWith("ies")) return `${noun.slice(0, -3)}y`;
  if (/(s|x|z|ch|sh)es$/.test(noun)) return noun.slice(0, -2);
  if (noun.endsWith("s") && !noun.endsWith("ss")) return noun.slice(0, -1);
  return noun;
};

/**
 * Whether a read endpoint's entity is the same as — or an ancestor of — the
 * mutation's entity, the rule for an entity-level default dependency. The read's
 * (singularized) noun must equal the mutation's, or be a prefix of it ending on a
 * PascalCase word boundary. So `updateAccount` refreshes `getAccount` /
 * `listAccounts`, `setAccountPrincipal` also refreshes the parent `getAccount`,
 * and `updateAccount` leaves child reads like `getAccountSettings` untouched.
 */
const isEntityRelatedRead = (
  mutationNoun: string,
  readNoun: string
): boolean => {
  if (!mutationNoun || !readNoun) return false;
  if (mutationNoun === readNoun) return true;
  if (!mutationNoun.startsWith(readNoun)) return false;
  // The char right after the shared prefix must start a new PascalCase word, so
  // "AccountPrincipal" matches ancestor "Account" but "Account" never matches "Acc".
  return /[A-Z]/.test(mutationNoun.charAt(readNoun.length));
};

/**
 * The entity-level default dependency keys for one mutation: the `[apiName,
 * readMethod]` invalidation prefix of every sibling read whose entity matches the
 * mutation's (per {@link isEntityRelatedRead}). Each prefix invalidates every
 * cached request of that read — regular and paginated alike.
 */
export const deriveEntityDependencyKeys = (params: {
  apiName: string;
  mutationMethodName: string;
  readMethodNames: string[];
}): QueryKey[] => {
  const mutationNoun = singularize(entityRootOf(params.mutationMethodName));
  if (!mutationNoun) return [];

  return params.readMethodNames
    .filter(readName =>
      isEntityRelatedRead(mutationNoun, singularize(entityRootOf(readName)))
    )
    .map(readName => [params.apiName, readName]);
};

/**
 * Precompute both default-dependency levels for a mutation: `api` is the
 * whole-api `[apiName]` prefix, `entity` the matched sibling reads.
 */
export const buildDefaultDependencies = (params: {
  apiName: string;
  mutationMethodName: string;
  readMethodNames: string[];
}): V3DefaultDependencies => ({
  api: [[params.apiName]],
  entity: deriveEntityDependencyKeys(params)
});

/** The default-dependency keys to inject for a given level (`"none"` → `[]`). */
export const resolveDefaultDependencies = (
  level: V3DefaultDependenciesLevel,
  defaults: V3DefaultDependencies
): QueryKey[] => (level === "none" ? [] : defaults[level]);
